import React, { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const INSTAGRAM_URL = "https://www.instagram.com/reagenvarross/";
const INSTAGRAM_HANDLE = "@reagenvarross";

/* ---------- Reveal-on-scroll hook ---------- */
const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
};

/* ---------- Navigation ---------- */
const NAV_LINKS = [
  { href: "#atelier", label: "Atelier", id: "atelier" },
  { href: "#prima-mora", label: "Prima Mora", id: "primamora" },
  { href: "#collection", label: "Lament of the Sirens", id: "collection" },
  { href: "#press", label: "Press", id: "press" },
  { href: "#red-carpet", label: "Red Carpet", id: "redcarpet" },
  { href: "#voices", label: "Voices", id: "voices" },
  { href: "#mentoring", label: "Mentoring", id: "mentoring" },
];

const Nav = ({ onEnquire }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  return (
    <nav
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        scrolled || open ? "py-3 bg-[rgba(72,60,50,0.85)] backdrop-blur-md border-b hairline" : "py-6 bg-transparent"
      }`}
    >
      <div className="max-w-[1480px] mx-auto px-6 md:px-10 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-center leading-none" onClick={() => setOpen(false)}>
          <img src="/brand/wordmark_ivory.png" alt="Reagen Varross London" className="h-9 md:h-14 2xl:h-16 w-auto block max-w-[55vw] object-contain" />
        </a>
        <div className="hidden min-[1700px]:flex items-center gap-2 font-eyebrow text-[13px] text-[var(--ivory)]">
          {NAV_LINKS.map((l) => (
            <a key={l.id} href={l.href} className="nav-link" data-testid={`nav-${l.id}`}>{l.label}</a>
          ))}
        </div>
        <div className="flex items-center gap-3 sm:gap-5">
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-instagram"
            className="text-[var(--ivory)]/80 hover:text-[var(--gold)] transition-colors text-sm"
            aria-label="Instagram"
          >
            <i className="fa-brands fa-instagram text-lg" />
          </a>
          <button onClick={onEnquire} data-testid="nav-enquire-btn" className="btn-luxe btn-glow !hidden sm:!inline-flex">
            Enquire
            <i className="fa-solid fa-arrow-right-long" />
          </button>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className="min-[1700px]:hidden text-[var(--ivory)] hover:text-[var(--gold)] transition-colors w-10 h-10 flex items-center justify-center"
          >
            <i className={`fa-solid ${open ? "fa-xmark" : "fa-bars-staggered"} text-xl`} />
          </button>
        </div>
      </div>

      <div
        data-testid="nav-menu-overlay"
        className={`min-[1700px]:hidden absolute inset-x-0 top-full h-[calc(100vh-100%)] bg-[var(--ink)] transition-opacity duration-500 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="h-full flex flex-col justify-between px-8 py-10 md:px-16 overflow-y-auto">
          <div className="flex flex-col gap-1 md:gap-3">
            {NAV_LINKS.map((l, i) => (
              <a
                key={l.id}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-${l.id}`}
                className={`font-display text-[var(--ivory)] text-3xl md:text-5xl py-3 border-b hairline hover:text-[var(--gold)] transition-all duration-500 ${
                  open ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
                }`}
                style={{ transitionDelay: open ? `${80 + i * 60}ms` : "0ms" }}
              >
                <span className="font-eyebrow text-[11px] text-[var(--gold)] mr-5 align-middle">0{i + 1}</span>
                {l.label}
              </a>
            ))}
          </div>
          <div className="flex items-center justify-between gap-6 pt-8">
            <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="font-eyebrow text-[12px] text-[var(--ivory)]/70 hover:text-[var(--gold)] transition-colors" data-testid="nav-mobile-instagram">
              <i className="fa-brands fa-instagram mr-2" />{INSTAGRAM_HANDLE}
            </a>
            <button onClick={() => { setOpen(false); onEnquire(); }} className="btn-luxe shrink-0" data-testid="nav-mobile-enquire-btn">
              Enquire
              <i className="fa-solid fa-arrow-right-long" />
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

/* ---------- Hero ---------- */
const Hero = ({ onEnquire }) => (
  <section id="top" data-testid="hero-section" className="relative h-[100vh] min-h-[720px] w-full overflow-hidden cursor-luxe" aria-label="Reagen Varross — British Haute Couture">
    {/* SEO-only H1 (visually hidden, semantically primary) */}
    <h1 className="sr-only">Reagen Varross — British Haute Couture · London Atelier · Bespoke Womenswear &amp; Made-to-Measure Gowns</h1>
    <div className="absolute inset-0">
      <img
        src="/brand/hero_gown.webp"
        alt="Reagen Varross couture — chartreuse silk chiffon gown embellished with crystals by Swarovski"
        className="w-full h-full object-cover object-[center_12%] kenburns"
      />
      <div
        className="absolute inset-0"
        style={{ background: "linear-gradient(to bottom, rgba(72,60,50,0.72) 0%, rgba(72,60,50,0.22) 28%, rgba(72,60,50,0.42) 58%, rgba(72,60,50,0.96) 100%)" }}
      />
      <div className="absolute inset-0 bg-gradient-to-tr from-[rgba(72,60,50,0.7)] via-transparent to-transparent" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(106,74,142,0.1),transparent_70%)] pointer-events-none" />
    </div>

    {/* Side vertical text */}
    <div className="hidden lg:block absolute left-8 top-1/2 -translate-y-1/2 vertical-rl font-eyebrow text-[12px] text-[var(--ivory)]/55 tracking-[0.5em]">
      London · Atelier Couture · MMXVIII
    </div>
    <div className="hidden lg:block absolute right-8 top-1/2 -translate-y-1/2 vertical-rl font-eyebrow text-[12px] text-[var(--gold)]/70 tracking-[0.5em]">
      By Appointment Only
    </div>

    <div className="relative z-10 h-full flex flex-col justify-end pb-24 md:pb-28 px-6 md:px-16">
      <div className="max-w-[1400px] mx-auto w-full">
        <div className="font-eyebrow text-[12px] sm:text-[16px] md:text-[22px] text-[var(--ivory)] mb-8 md:mb-12 flex items-center gap-3 md:gap-6 flex-wrap" data-testid="hero-eyebrow" style={{ textShadow: "0 2px 22px rgba(72,60,50,0.85), 0 0 2px rgba(0,0,0,0.4)" }}>
          <img src="/brand/monogram_gold.png" alt="RV" className="h-10 w-10 md:h-20 md:w-20 lg:h-24 lg:w-24 drop-shadow-[0_4px_22px_rgba(72,60,50,0.7)]" />
          <span className="hidden sm:inline-block w-10 md:w-16 h-px bg-[var(--gold)]" />
          <span>A British House of Haute Couture</span>
        </div>
        <h1
          data-testid="hero-title"
          aria-hidden="true"
          className="font-display text-[var(--ivory)] text-[2.6rem] xs:text-[3.2rem] sm:text-[4.4rem] md:text-[6.4rem] lg:text-[8rem] xl:text-[8.6rem] leading-[0.95] tracking-[-0.02em] break-words"
        >
          Cloth, devoted
          <br />
          to the <span className="font-italic-display text-[var(--gold)]">extraordinary</span>.
        </h1>
        <div className="mt-10 grid md:grid-cols-12 gap-8 items-end">
          <p className="md:col-span-5 font-body text-[var(--ivory)]/80 text-lg md:text-xl leading-relaxed">
            Reagen Varross is a London-based couturier creating singular garments for a discreet
            circle of women — gowns built by one pair of hands, over a thousand hours, with the
            patience of an older century.
          </p>
          <div className="md:col-span-7 md:flex md:justify-end gap-6 items-center">
            <button onClick={onEnquire} data-testid="hero-enquire-btn" className="btn-luxe btn-glow">
              Begin a Private Commission
              <i className="fa-solid fa-arrow-right-long" />
            </button>
            <a href="#collection" className="btn-ghost ml-6 hidden md:inline-block" data-testid="hero-collection-link">
              View Lament of the Sirens
            </a>
          </div>
        </div>
      </div>
    </div>

    {/* Scroll cue — hidden on mobile to avoid overlap with CTA */}
    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex-col items-center gap-3 z-10 hidden md:flex">
      <span className="font-eyebrow text-[12px] text-[var(--ivory)]/50">Scroll</span>
      <span className="w-px h-12 bg-gradient-to-b from-[var(--gold)] to-transparent" />
    </div>
  </section>
);

/* ---------- Press marquee ---------- */
const PressBar = () => {
  const items = ["Vogue", "L'Officiel Arabia", "L'Officiel UK", "Avesso Magazine", "Style Doctors", "Paris Haute Couture Week", "The Frick Collection"];
  return (
    <section data-testid="press-marquee" className="border-y hairline py-7 bg-[var(--ink-soft)] overflow-hidden">
      <div className="flex w-max marquee-track">
        {[...items, ...items, ...items].map((t, i) => (
          <div key={i} className="flex items-center gap-12 pr-12">
            <span className="font-italic-display text-[var(--ivory)]/55 text-2xl md:text-3xl whitespace-nowrap">{t}</span>
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--gold)]/60" />
          </div>
        ))}
      </div>
    </section>
  );
};

/* ---------- Atelier story ---------- */
const Atelier = () => (
  <section id="atelier" data-testid="atelier-section" className="relative py-28 md:py-40 px-6 md:px-16">
    <div className="max-w-[1400px] mx-auto">
      {/* Images row — full width */}
      <div className="reveal mb-12 md:mb-16">
        <div className="grid grid-cols-12 gap-3 md:gap-4">
          <div className="col-span-8 editorial-frame aspect-[3/4]">
            <img src="/brand/campaign2.jpg" alt="Reagen Varross — couture designer" className="w-full h-full object-cover" />
          </div>
          <div className="col-span-4 flex flex-col gap-3 md:gap-4">
            <div className="editorial-frame aspect-square">
              <img src="/brand/campaign9.jpg" alt="Reagen Varross atelier" className="w-full h-full object-cover" />
            </div>
            <div className="editorial-frame aspect-square">
              <img src="/brand/campaign10.jpg" alt="Couture look" className="w-full h-full object-cover" />
            </div>
            <div className="border hairline-gold p-3 md:p-4 mt-1">
              <div className="font-italic-display text-[var(--gold)] text-base md:text-lg leading-snug">"Without quality, there is no luxury."</div>
              <div className="font-eyebrow text-[11px] md:text-[13px] text-[var(--ivory)]/50 mt-2 md:mt-3">— Reagen Varross, L'Officiel UK</div>
            </div>
          </div>
        </div>
      </div>

      {/* Text block — full width below */}
      <div className="reveal grid md:grid-cols-12 gap-8 md:gap-12 items-start">
        <div className="md:col-span-5">
          <div className="font-eyebrow text-[13px] text-[var(--gold)] mb-5">— The Atelier</div>
          <h2 className="font-display text-[var(--ivory)] text-4xl md:text-6xl leading-[0.95]">
            One hand. <br />
            <span className="font-italic-display text-[var(--gold)]">One thousand hours.</span>
          </h2>
        </div>
        <div className="md:col-span-7">
          <p className="font-body text-[var(--ivory)]/80 text-lg leading-relaxed mb-6 drop-cap">
            Reagen Varross founded his eponymous house in late 2018, refusing the grammar of mass
            production for something far quieter — couture as a private dialogue. From a studio in
            London, he draws, drapes, cuts, and stitches each commission himself for a small
            constellation of stylish women on three continents.
          </p>
          <p className="font-body text-[var(--ivory)]/65 text-base leading-relaxed">
            A single gown can hold over a thousand hours of work. Hand-rolled silk, ribbon
            embroidery, panels of organza built like architecture. Nothing leaves the atelier
            until it is felt to be inevitable.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-6 md:gap-8 max-w-2xl">
            <div>
              <div className="font-display text-[var(--gold)] text-3xl md:text-5xl">1,000+</div>
              <div className="font-eyebrow text-[11px] md:text-[12px] text-[var(--ivory)]/55 mt-2">Hours / Garment</div>
            </div>
            <div>
              <div className="font-display text-[var(--gold)] text-3xl md:text-5xl">2018</div>
              <div className="font-eyebrow text-[11px] md:text-[12px] text-[var(--ivory)]/55 mt-2">House Founded</div>
            </div>
            <div>
              <div className="font-display text-[var(--gold)] text-3xl md:text-5xl">London</div>
              <div className="font-eyebrow text-[11px] md:text-[12px] text-[var(--ivory)]/55 mt-2">Atelier</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ---------- Collection ---------- */
const Collection = () => (
  <section id="collection" data-testid="collection-section" className="relative py-28 md:py-40 px-6 md:px-16 bg-[var(--ink-soft)] border-y hairline overflow-hidden">
    <div className="absolute -top-40 -right-40 w-[40rem] h-[40rem] rounded-full bg-[var(--amethyst-deep)] opacity-30 blur-3xl pointer-events-none" />
    <div className="max-w-[1400px] mx-auto relative">
      <div className="grid md:grid-cols-12 gap-8 mb-16 items-end reveal">
        <div className="md:col-span-7">
          <div className="font-eyebrow text-[13px] text-[var(--gold)] mb-6">— Paris Haute Couture Week · MMXXV</div>
          <h2 className="font-display text-[var(--ivory)] text-5xl md:text-7xl leading-[0.95]">
            Lament of the
            <br />
            <span className="font-italic-display text-[var(--amethyst)]">Sirens</span>.
          </h2>
        </div>
        <div className="md:col-span-5">
          <p className="font-body text-[var(--ivory)]/80 text-lg md:text-xl leading-relaxed">
            An ocean-deep collection — silver-veined silks, amethyst tulle, plissé that moves like
            tide. Premiered in Paris, it is the house's love letter to a sea we have charted less
            than the stars.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-12 gap-6 reveal">
        <div className="md:col-span-7 editorial-frame aspect-[4/5]">
          <img
            src="/brand/campaign7.jpg"
            alt="Lament of the Sirens — look one"
            className="w-full h-full object-cover"
          />
          <div className="absolute bottom-6 left-6 right-6 hidden md:flex justify-between items-end">
            <span className="font-italic-display text-2xl text-[var(--ivory)]">Look I — Medusa</span>
            <span className="font-eyebrow text-[12px] text-[var(--ivory)]/70">Amethyst Silk · Hand Pleated</span>
          </div>
        </div>
        <div className="md:col-span-5 flex flex-col gap-6">
          <div className="editorial-frame aspect-[4/5] flex-1">
            <img
              src="/brand/campaign8.jpg"
              alt="Lament of the Sirens — look two"
              className="w-full h-full object-cover"
            />
          </div>
          <a href="#red-carpet" className="border hairline p-8 bg-[rgba(72,60,50,0.6)] block hover:border-[var(--amethyst)] transition-colors duration-500" data-testid="collection-redcarpet-link">
            <div className="font-eyebrow text-[12px] text-[var(--gold)] mb-4">— Featured Piece</div>
            <div className="font-display text-[var(--ivory)] text-3xl md:text-4xl mb-3">The Amethyst Jellyfish</div>
            <div className="font-body text-[var(--ivory)]/70 text-sm leading-relaxed">
              Cited by <em>Vogue</em> as it "floated among Flora Yukhnovich's Four Seasons" at the
              Frick Collection's All Hallows' Eve gala, New York.
            </div>
            <div className="font-eyebrow text-[15px] text-[var(--amethyst)] mt-5 flex items-center gap-2">
              See it on the carpet <i className="fa-solid fa-arrow-right-long" />
            </div>
          </a>
        </div>
      </div>

      <div className="mt-16 flex flex-col md:flex-row gap-8 items-start md:items-center justify-between reveal">
        <p className="font-italic-display text-[var(--ivory)]/70 text-2xl max-w-2xl leading-snug">
          "We know more about space than we do our own seas. That is where this collection lives."
        </p>
        <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="btn-luxe" data-testid="collection-instagram-btn">
          See the full lookbook
          <i className="fa-brands fa-instagram" />
        </a>
      </div>
    </div>
  </section>
);

/* ---------- Prima Mora (latest collection) ---------- */
const PRIMA_MORA_LOOKS = [
  { src: "/primamora/sapphire_detail.webp", alt: "Prima Mora — sapphire silk chiffon gown, bodice embellished with crystals by Swarovski" },
  { src: "/primamora/chartreuse_detail.webp", alt: "Prima Mora — chartreuse silk chiffon gown, bodice embellished with crystals by Swarovski" },
  { src: "/primamora/noir_corset.webp", alt: "Prima Mora — black corset with crystal drops by Swarovski over a chartreuse satin skirt" },
  { src: "/primamora/crystal_drops.webp", alt: "Prima Mora — detail of crystal drops by Swarovski" },
  { src: "/primamora/blue_gown.webp", alt: "Prima Mora — sapphire silk chiffon gown in the atelier" },
  { src: "/primamora/reagen_chartreuse.webp", alt: "Reagen Varross beside the chartreuse Prima Mora gown at dusk" },
  { src: "/primamora/conversation.webp", alt: "Reagen Varross in conversation with a patron before the black corset and chartreuse satin skirt" },
  { src: "/primamora/patrons_three.webp", alt: "Reagen Varross with patrons beside the sapphire Prima Mora gown" },
];

const PrimaMora = ({ onEnquire }) => (
  <section id="prima-mora" data-testid="primamora-section" className="relative py-28 md:py-40 px-6 md:px-16 bg-[var(--ink)] border-t hairline overflow-hidden">
    <div className="absolute -top-56 -left-56 w-[46rem] h-[46rem] rounded-full bg-[var(--gold-deep)] opacity-15 blur-3xl pointer-events-none" />
    <div className="max-w-[1400px] mx-auto relative">
      <div className="grid md:grid-cols-12 gap-8 mb-16 items-end reveal">
        <div className="md:col-span-7">
          <div className="font-eyebrow text-[13px] text-[var(--gold)] mb-6" data-testid="primamora-eyebrow">— The Latest Collection · MMXXVI</div>
          <h2 className="font-display text-[var(--ivory)] text-5xl md:text-7xl leading-[0.95]" data-testid="primamora-title">
            Prima
            <br />
            <span className="font-italic-display text-[var(--gold)]">Mora</span>.
          </h2>
        </div>
        <div className="md:col-span-5">
          <div className="font-eyebrow text-[12px] text-[var(--ivory)]/60 mb-4">Latin · "the first pause"</div>
          <p className="font-body text-[var(--ivory)]/80 text-lg md:text-xl leading-relaxed">
            A poetic nod to a first gesture before a ripple spreads. The delay from the initial
            impact of the droplet, where energy and inspiration surge before cascading ripples
            emerge — signifying the act of kindness, or an unexpected gesture, that enables the
            continuation of a dream.
          </p>
        </div>
      </div>

      <div className="editorial-frame aspect-[16/9] md:aspect-[21/9] reveal" data-testid="primamora-hero-image">
        <img src="/primamora/skyline.webp" alt="Prima Mora chartreuse gown on a mannequin before the New York skyline" className="w-full h-full object-cover" />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[rgba(72,60,50,0.85)] to-transparent pointer-events-none" />
        <div className="absolute bottom-6 left-6 right-6 hidden md:flex justify-between items-end">
          <span className="font-italic-display text-2xl text-[var(--ivory)]">Revealed in Manhattan · MMXXVI</span>
          <span className="font-eyebrow text-[12px] text-[var(--ivory)]/90">Silk Chiffon · Embellished with Crystals by Swarovski</span>
        </div>
      </div>

      <div className="mt-6 grid md:grid-cols-12 gap-6 md:gap-10 items-center border-b hairline pb-10 reveal" data-testid="primamora-reveal-note">
        <div className="md:col-span-4 font-eyebrow text-[13px] text-[var(--gold)]">— The Reveal · New York</div>
        <p className="md:col-span-8 font-italic-display text-[var(--ivory)] text-2xl md:text-3xl leading-snug">
          Prima Mora was revealed in Manhattan to a group of exclusive private clients in a New York
          penthouse — the brand's first appearance in the city.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mt-6 reveal">
        {PRIMA_MORA_LOOKS.map((look, i) => (
          <figure key={look.src} className="editorial-frame aspect-[3/4] m-0" data-testid={`primamora-look-${i}`}>
            <img src={look.src} alt={look.alt} className="w-full h-full object-cover" loading="lazy" />
          </figure>
        ))}
      </div>

      <div className="mt-6 border hairline bg-[var(--ink-soft)] p-8 md:p-14 grid md:grid-cols-12 gap-10 reveal" data-testid="primamora-note">
        <div className="md:col-span-6">
          <div className="font-eyebrow text-[12px] text-[var(--gold)] mb-6">— A Note from Reagen</div>
          <p className="font-body text-[var(--ivory)]/85 text-lg md:text-xl leading-relaxed drop-cap">
            For this collection, that moment came from the unwavering belief and support of my
            clients and patrons — the kindness and the generosity they so selflessly showed.
          </p>
          <p className="font-body text-[var(--ivory)]/85 text-lg md:text-xl leading-relaxed mt-6">
            Their support created my prima mora:
          </p>
        </div>
        <div className="md:col-span-6 flex flex-col justify-between">
          <p className="font-italic-display text-[var(--gold)] text-3xl md:text-4xl leading-snug">
            a breath of astonishment, gratitude, and clarity before a surge of new creative energy.
          </p>
          <div className="mt-10 flex items-center gap-4">
            <img src="/brand/monogram_gold.png" alt="RV" className="h-10 w-10" />
            <span className="font-eyebrow text-[12px] text-[var(--ivory)]/60">Reagen Varross · London</span>
          </div>
          <div className="mt-8 pt-6 border-t hairline" data-testid="primamora-patron-credit">
            <div className="font-eyebrow text-[12px] text-[var(--gold)] mb-2">— With Gratitude to</div>
            <div className="font-italic-display text-[var(--ivory)] text-2xl">Miss CK &amp; Mr &amp; Mrs W</div>
          </div>
        </div>
      </div>

      <div className="mt-16 flex flex-col md:flex-row gap-8 items-start md:items-center justify-between reveal">
        <p className="font-italic-display text-[var(--ivory)]/70 text-2xl max-w-2xl leading-snug">
          "Every ripple begins with a single, generous gesture."
        </p>
        <button onClick={onEnquire} className="btn-luxe" data-testid="primamora-enquire-btn">
          Commission from Prima Mora
          <i className="fa-solid fa-arrow-right-long" />
        </button>
      </div>
    </div>
  </section>
);

/* ---------- Press features ---------- */
const Press = () => {
  const features = [
    {
      outlet: "VOGUE",
      date: "October 2025",
      title: "An amethyst Reagen Varross jellyfish floated among Flora Yukhnovich's Four Seasons.",
      tag: "The Frick Collection · All Hallows' Eve",
      href: "https://www.vogue.com/article/best-celebrity-halloween-parties",
      accent: "amethyst",
    },
    {
      outlet: "L'OFFICIEL",
      date: "Interview · UK",
      title: "Reagen Varross — A Personal Approach to Haute Couture.",
      tag: "On founding a house and the discipline of solitude.",
      href: "https://lofficieluk.com/format/INTERVIEWReagenVarrossAPersonalApproachtoHauteCouture.php?id=2440&table_name=master_table",
      accent: "gold",
    },
    {
      outlet: "L'OFFICIEL ARABIA",
      date: "Featured Designer",
      title: "A British couturier guarding the slow craft of the gown.",
      tag: "Profile · Atelier Visit",
      href: "https://www.lofficielarabia.com",
      accent: "gold",
    },
    {
      outlet: "AVESSO MAGAZINE",
      date: "2025",
      title: "Lament of the Sirens — Inside Reagen Varross's ocean-inspired couture.",
      tag: "Studio Visit",
      href: "https://www.avessomagazine.com/fashion/lament-of-the-sirens-inside-reagen-varross-ocean-inspired-couture",
      accent: "ivory",
    },
    {
      outlet: "POINT DE VUE",
      date: "Paris · Juillet 2025",
      title: "Reagen Varross — les confidences d'un couturier en vogue.",
      tag: "Entretien · Semaine de la Haute Couture",
      href: "https://www.pointdevue.fr/lifestyle/mode/reagen-varross-les-confidences-dun-couturier-en-vogue",
      accent: "gold",
    },
    {
      outlet: "BBC NEWS",
      date: "September 2024",
      title: "Bride married in dream dress made by her brother.",
      tag: "120 hours, by hand · Shropshire · BBC West Midlands",
      href: "https://www.bbc.co.uk/news/articles/cqjl91wdenpo",
      accent: "ivory",
    },
  ];
  return (
    <section id="press" data-testid="press-section" className="py-28 md:py-40 px-6 md:px-16">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between mb-16 reveal">
          <div>
            <div className="font-eyebrow text-[13px] text-[var(--gold)] mb-6">— Press & Recognition</div>
            <h2 className="font-display text-[var(--ivory)] text-5xl md:text-7xl leading-[0.95]">
              In the <span className="font-italic-display text-[var(--gold)]">papers</span> of record.
            </h2>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-px bg-[rgba(241,234,216,0.12)] border hairline">
          {features.map((f, i) => (
            <a
              key={i}
              href={f.href}
              target="_blank"
              rel="noreferrer"
              data-testid={`press-card-${i}`}
              className="group bg-[var(--ink)] p-10 md:p-14 reveal hover:bg-[var(--ink-soft)] transition-colors duration-700 block"
            >
              <div className="flex items-center justify-between mb-10">
                <div className="font-display text-2xl tracking-[0.18em] text-[var(--ivory)]">{f.outlet}</div>
                <div className="font-eyebrow text-[12px] text-[var(--ivory)]/50">{f.date}</div>
              </div>
              <p className="font-italic-display text-[var(--ivory)] text-2xl md:text-3xl leading-snug mb-8">
                "{f.title}"
              </p>
              <div className="flex items-center justify-between pt-6 border-t hairline">
                <span className="font-eyebrow text-[12px] text-[var(--ivory)]/60">{f.tag}</span>
                <span className="font-eyebrow text-[12px] text-[var(--gold)] flex items-center gap-2 group-hover:gap-4 transition-all">
                  Read <i className="fa-solid fa-arrow-right-long" />
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Red Carpet ---------- */
const RedCarpet = () => (
  <section id="red-carpet" data-testid="redcarpet-section" className="relative py-28 md:py-40 px-6 md:px-16 bg-[var(--ink)] overflow-hidden">
    <div className="absolute -top-40 -right-40 w-[42rem] h-[42rem] rounded-full bg-[var(--amethyst-deep)] opacity-35 blur-3xl pointer-events-none" />
    <div className="absolute -bottom-40 -left-40 w-[36rem] h-[36rem] rounded-full bg-[var(--amethyst-deep)] opacity-20 blur-3xl pointer-events-none" />

    <div className="max-w-[1480px] mx-auto relative">
      <div className="mb-20 reveal">
        <div className="font-eyebrow text-[13px] text-[var(--gold)] mb-6">— Red Carpet Moments</div>
        <h2 className="font-display text-[var(--ivory)] text-5xl md:text-7xl leading-[0.95] max-w-4xl">
          The house on <span className="font-italic-display text-[var(--gold)]">the carpet</span>.
        </h2>
      </div>

      {/* MOMENT I — The Frick Collection (Vogue) */}
      <div className="grid md:grid-cols-12 gap-10 mb-12 items-end reveal">
        <div className="md:col-span-7">
          <div className="font-eyebrow text-[15px] text-[var(--amethyst)] mb-4 tracking-[0.34em]">
            I · The Frick Collection · All Hallows' Eve · New York · 2025
          </div>
          <h3 className="font-display text-[var(--ivory)] text-4xl md:text-6xl leading-[0.95]">
            Casey Kohlberg in the <br />
            <span className="font-italic-display text-[var(--amethyst)]">Amethyst Jellyfish</span> gown.
          </h3>
        </div>
        <div className="md:col-span-5">
          <p className="font-body text-[var(--ivory)]/80 text-lg md:text-xl leading-relaxed">
            Cited by <em>Vogue</em> as it "floated among Flora Yukhnovich's Four Seasons" — an
            off-shoulder organza dome printed with the deep ocean, anchored in plum velvet,
            hand-built for the Frick's restored galleries.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-12 gap-4 mb-24 reveal">
        <div className="md:col-span-6 editorial-frame aspect-[4/5]">
          <img src="/redcarpet/ck1.jpg" alt="Casey Kohlberg at the Frick Collection in the Reagen Varross amethyst jellyfish gown" className="w-full h-full object-cover" />
          <div className="absolute bottom-5 left-5 right-5 hidden md:flex items-end justify-between">
            <span className="font-italic-display text-xl md:text-2xl text-[var(--ivory)]">The Frick · Fountain</span>
            <span className="font-eyebrow text-[12px] text-[var(--ivory)]/70">Vogue · 2025</span>
          </div>
        </div>
        <div className="md:col-span-6 editorial-frame aspect-[4/5]">
          <img src="/redcarpet/ck5.jpg" alt="Amethyst jellyfish gown — full silhouette" className="w-full h-full object-cover" />
        </div>
        <div className="md:col-span-12 editorial-frame aspect-[21/9] mt-2">
          <img src="/redcarpet/ck3.jpg" alt="The Frick Collection All Hallows' Eve — group portrait" className="w-full h-full object-cover" />
        </div>
      </div>

      {/* Divider */}
      <div className="flex items-center gap-6 mb-16 reveal">
        <span className="flex-1 h-px bg-[var(--gold)]/30" />
        <i className="fa-solid fa-feather text-[var(--gold)]/60" />
        <span className="flex-1 h-px bg-[var(--gold)]/30" />
      </div>

      {/* MOMENT II — Fashion Trust U.S. */}
      <div className="grid md:grid-cols-12 gap-10 mb-12 items-end reveal">
        <div className="md:col-span-7">
          <div className="font-eyebrow text-[12px] text-[var(--gold)] mb-4 tracking-[0.4em]">
            II · Fashion Trust U.S. Awards · Los Angeles · 2026
          </div>
          <h3 className="font-display text-[var(--ivory)] text-4xl md:text-6xl leading-[0.95]">
            B Åkerlund in the <br />
            <span className="font-italic-display text-[var(--gold)]">Black Peplum & Gown-Trouser</span>.
          </h3>
        </div>
        <div className="md:col-span-5">
          <p className="font-body text-[var(--ivory)]/80 text-lg md:text-xl leading-relaxed">
            The stylist B Åkerlund opened the Fashion Trust U.S. red carpet in the house's
            feather-trimmed peplum jacket and signature gown-trouser — a sculpted couture
            statement alongside Fergie on the front row.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-12 gap-4 mb-24 reveal">
        <div className="md:col-span-5 editorial-frame aspect-[3/4]">
          <img src="/redcarpet/ft3.jpg" alt="B Åkerlund at Fashion Trust US 2026 in Reagen Varross" className="w-full h-full object-cover" />
        </div>
        <div className="md:col-span-7 grid grid-cols-2 gap-4">
          <div className="editorial-frame aspect-[3/4] col-span-2">
            <img src="/redcarpet/ft1.jpg" alt="B Åkerlund and Fergie — Fashion Trust US" className="w-full h-full object-cover object-top" />
          </div>
          <div className="editorial-frame aspect-square">
            <img src="/redcarpet/ft2.jpg" alt="Fashion Trust US signage moment" className="w-full h-full object-cover" />
          </div>
          <div className="editorial-frame aspect-square">
            <img src="/redcarpet/ft4.jpg" alt="B Åkerlund detail — Reagen Varross feather peplum" className="w-full h-full object-cover" />
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="flex items-center gap-6 mb-16 reveal">
        <span className="flex-1 h-px bg-[var(--gold)]/30" />
        <i className="fa-solid fa-feather text-[var(--gold)]/60" />
        <span className="flex-1 h-px bg-[var(--gold)]/30" />
      </div>

      {/* MOMENT III — Cannes Film Festival */}
      <div className="grid md:grid-cols-12 gap-10 mb-12 items-end reveal">
        <div className="md:col-span-7">
          <div className="font-eyebrow text-[15px] text-[var(--amethyst)] mb-4 tracking-[0.34em]">
            III · 79th Festival de Cannes · Closing Ceremony · 2026
          </div>
          <h3 className="font-display text-[var(--ivory)] text-4xl md:text-6xl leading-[0.95]">
            Roselyn Sanchez in the <br />
            <span className="font-italic-display text-[var(--amethyst)]">Amethyst Velvet</span> gown.
          </h3>
        </div>
        <div className="md:col-span-5">
          <p className="font-body text-[var(--ivory)]/80 text-lg md:text-xl leading-relaxed">
            The actress graced the red carpet for the finale of the 79th Cannes Film Festival
            wearing the house's silk velvet gown from the 2025 couture collection that debuted
            in Paris.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-12 gap-4 reveal">
        <div className="md:col-span-6 editorial-frame aspect-[4/5]">
          <img src="/redcarpet/cn2.jpg" alt="Roselyn Sanchez at the 79th Cannes Film Festival closing ceremony in Reagen Varross amethyst velvet" className="w-full h-full object-cover" />
        </div>
        <div className="md:col-span-6 editorial-frame aspect-[4/5]">
          <img src="/redcarpet/cn3.jpg" alt="Roselyn Sanchez — amethyst velvet gown detail" className="w-full h-full object-cover" />
        </div>
        <div className="md:col-span-7 editorial-frame aspect-[16/10] mt-2">
          <img src="/redcarpet/cn4.jpg" alt="Roselyn Sanchez on the Cannes red carpet" className="w-full h-full object-cover" />
        </div>
        <div className="md:col-span-5 editorial-frame aspect-[16/10] mt-2">
          <img src="/redcarpet/cn1.jpg" alt="Roselyn Sanchez wearing Reagen Varross at Cannes" className="w-full h-full object-cover" />
        </div>
      </div>

      <div className="mt-14 flex flex-col md:flex-row gap-6 items-start md:items-center justify-between reveal">
        <p className="font-italic-display text-[var(--ivory)]/70 text-xl md:text-2xl max-w-2xl leading-snug">
          "Couture as it should arrive — quietly, on the carpet, and impossible to forget."
        </p>
        <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="redcarpet-instagram">
          See more on Instagram
        </a>
      </div>
    </div>
  </section>
);
const Voices = () => {
  const quotes = [
    {
      text: "If you want a couture piece from me, it's a journey we will embark on together. That is the only way to avoid subconscious imitation and block out external trends.",
      author: "Reagen Varross",
      role: "L'Officiel UK · January 2025",
    },
    {
      text: "I genuinely love my clients dearly. I am the only one at the helm, so I know them all personally — every fitting, every consultation, every seam.",
      author: "On the Atelier",
      role: "Interview with Tanja Beljanski",
    },
    {
      text: "Couture, to me, is timeless — no matter how long it has been in an archive. I never pay attention to trends or mass hype; they pass quickly and don't age well.",
      author: "On Time & Memory",
      role: "L'Officiel UK · Paris Haute Couture Week",
    },
  ];
  return (
    <section id="voices" data-testid="voices-section" className="relative py-28 md:py-40 px-6 md:px-16 bg-[var(--ink-soft)] border-y hairline">
      <div className="max-w-[1400px] mx-auto">
        <div className="text-center mb-20 reveal">
          <div className="font-eyebrow text-[13px] text-[var(--gold)] mb-6">— In His Own Words</div>
          <h2 className="font-display text-[var(--ivory)] text-5xl md:text-7xl leading-[0.95]">
            On the <span className="font-italic-display text-[var(--gold)]">craft</span>.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-10 md:gap-14">
          {quotes.map((q, i) => (
            <figure key={i} data-testid={`testimonial-${i}`} className="reveal relative">
              <div className="font-italic-display text-[var(--gold)] text-7xl leading-none mb-4 select-none">"</div>
              <blockquote className="font-body text-[var(--ivory)]/85 text-lg md:text-xl leading-relaxed mb-8">
                {q.text}
              </blockquote>
              <figcaption className="pt-6 border-t hairline">
                <div className="font-display text-[var(--ivory)] text-lg">{q.author}</div>
                <div className="font-eyebrow text-[12px] text-[var(--ivory)]/55 mt-2">{q.role}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Fashion Mentoring ---------- */
const MENTORING_SERVICES = [
  { title: "One-to-One Mentoring", format: "Online", price: "£150 per hour", desc: "Private sessions on design direction, collection development and the realities of building a fashion business — in any area of the industry." },
  { title: "Portfolio & Collection Review", format: "Online", price: "£80 per hour", desc: "Honest, detailed critique of portfolios, lookbooks and graduate collections — what to keep, cut and sharpen." },
  { title: "Draping to Patterns", format: "In person", price: "£300 per 2-hour session", desc: "From the stand to the paper: draping a silhouette by hand, then translating it into a precise, repeatable pattern." },
  { title: "Garment Technology", format: "Online or in person", price: "£80 per hour", desc: "Construction, internal structure, fabric behaviour and finishing — the technical knowledge that makes a design wearable." },
  { title: "Show & Presentation Prep", format: "Online or in person", price: "£90 per hour", desc: "From runway to showroom to an intimate private reveal — casting, styling, pacing and the room itself." },
];

const MENTORING_STAGES = ["Student", "Recent graduate", "Emerging brand", "Established brand"];
const MENTORING_FORMATS = ["Online", "In person", "Either"];

const MentoringForm = () => {
  const empty = { name: "", email: "", stage: "", interest: "", format: "", location: "", message: "" };
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and a note on your goals are required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/mentoring`, form);
      setSubmitted(true);
      toast.success("Your mentoring enquiry has reached Reagen.");
      setForm(empty);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="mentoring-success" className="border hairline-gold p-12 text-center">
        <i className="fa-solid fa-pen-nib text-[var(--gold)] text-3xl mb-6" />
        <h3 className="font-display text-[var(--ivory)] text-3xl mb-4">Thank you.</h3>
        <p className="font-body text-[var(--ivory)]/70 max-w-md mx-auto">
          Reagen reviews every mentoring request personally and will reply within two weeks.
        </p>
        <button onClick={() => setSubmitted(false)} className="btn-ghost mt-8" data-testid="mentoring-reset-btn">Send another</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate data-testid="mentoring-form" className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
      <div>
        <label className="font-eyebrow text-[12px] text-[var(--ivory)]/55">Name *</label>
        <input data-testid="ment-name" className="input-luxe" value={form.name} onChange={change("name")} placeholder="Your full name" required />
      </div>
      <div>
        <label className="font-eyebrow text-[12px] text-[var(--ivory)]/55">Email *</label>
        <input data-testid="ment-email" type="email" className="input-luxe" value={form.email} onChange={change("email")} placeholder="you@domain.com" required />
      </div>
      <div>
        <label className="font-eyebrow text-[12px] text-[var(--ivory)]/55">Where are you now?</label>
        <select data-testid="ment-stage" className="input-luxe select-luxe" value={form.stage} onChange={change("stage")}>
          <option value="">Select a stage</option>
          {MENTORING_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div>
        <label className="font-eyebrow text-[12px] text-[var(--ivory)]/55">Area of interest</label>
        <select data-testid="ment-interest" className="input-luxe select-luxe" value={form.interest} onChange={change("interest")}>
          <option value="">Select a service</option>
          {MENTORING_SERVICES.map((s) => <option key={s.title} value={s.title}>{s.title}</option>)}
        </select>
      </div>
      <div>
        <label className="font-eyebrow text-[12px] text-[var(--ivory)]/55">Preferred format</label>
        <select data-testid="ment-format" className="input-luxe select-luxe" value={form.format} onChange={change("format")}>
          <option value="">Online or in person</option>
          {MENTORING_FORMATS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div>
        <label className="font-eyebrow text-[12px] text-[var(--ivory)]/55">Location</label>
        <input data-testid="ment-location" className="input-luxe" value={form.location} onChange={change("location")} placeholder="City, Country" />
      </div>
      <div className="md:col-span-2">
        <label className="font-eyebrow text-[12px] text-[var(--ivory)]/55">Your goals *</label>
        <textarea data-testid="ment-message" className="input-luxe min-h-[110px] resize-none" value={form.message} onChange={change("message")} placeholder="Where are you, where do you want to be, and what stands in the way..." required />
      </div>
      <div className="md:col-span-2 flex flex-col sm:flex-row gap-6 items-start sm:items-center justify-between pt-4">
        <p className="font-body text-[var(--ivory)]/45 text-xs italic">Places are limited each season. Sessions are invoiced after confirmation.</p>
        <button type="submit" disabled={submitting} data-testid="ment-submit-btn" className="btn-luxe disabled:opacity-50">
          {submitting ? "Sending..." : "Request Mentoring"}
          <i className="fa-solid fa-pen-nib" />
        </button>
      </div>
    </form>
  );
};

const Mentoring = () => (
  <section id="mentoring" data-testid="mentoring-section" className="relative py-28 md:py-40 px-6 md:px-16 bg-[var(--ink)] border-t hairline overflow-hidden">
    <div className="absolute -bottom-56 -right-56 w-[46rem] h-[46rem] rounded-full bg-[var(--gold-deep)] opacity-15 blur-3xl pointer-events-none" />
    <div className="max-w-[1400px] mx-auto relative">
      <div className="grid md:grid-cols-12 gap-8 mb-16 items-end reveal">
        <div className="md:col-span-7">
          <div className="font-eyebrow text-[13px] text-[var(--gold)] mb-6">— Fashion Mentoring · For New Brands &amp; Students</div>
          <h2 className="font-display text-[var(--ivory)] text-5xl md:text-7xl leading-[0.95]" data-testid="mentoring-title">
            Pass the
            <br />
            <span className="font-italic-display text-[var(--gold)]">thread</span>.
          </h2>
        </div>
        <div className="md:col-span-5">
          <p className="font-body text-[var(--ivory)]/80 text-lg md:text-xl leading-relaxed">
            Reagen offers a small number of mentoring places each season to students and emerging
            brands across every area of fashion — online for strategy and critique, in person at the
            atelier for the practical craft that can only be learned by hand.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-12 gap-6 reveal">
        <div className="md:col-span-6 flex flex-col gap-6">
          <div className="editorial-frame aspect-[4/3]" data-testid="mentoring-image">
            <img src="/mentoring/lecture_draping.webp" alt="Reagen Varross demonstrating draping on the stand during a masterclass" className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[rgba(72,60,50,0.85)] to-transparent pointer-events-none" />
            <div className="absolute bottom-6 left-6 right-6 hidden md:flex justify-between items-end">
              <span className="font-italic-display text-2xl text-[var(--ivory)]">Learn by hand</span>
              <span className="font-eyebrow text-[12px] text-[var(--ivory)]/90">Masterclass · Draping to Patterns</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="editorial-frame aspect-[3/2]" data-testid="mentoring-image-audience">
              <img src="/mentoring/audience.webp" alt="Students and designers gathered for a Reagen Varross lecture" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="editorial-frame aspect-[3/2]" data-testid="mentoring-image-certificates">
              <img src="/mentoring/certificates.webp" alt="Students holding their certificates after a Reagen Varross masterclass" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>
        <div className="md:col-span-6 border hairline flex flex-col">
          {MENTORING_SERVICES.map((s, i) => (
            <div key={s.title} className={`flex-1 hover:bg-[var(--ink-soft)] transition-colors duration-500 p-6 md:p-8 ${i > 0 ? "border-t hairline" : ""}`} data-testid={`mentoring-service-${i}`}>
              <div className="flex items-start gap-5">
                <span className="font-eyebrow text-[11px] text-[var(--gold)] pt-2 shrink-0">0{i + 1}</span>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                    <h3 className="font-display text-[var(--ivory)] text-2xl leading-tight">{s.title}</h3>
                    <span className="font-eyebrow text-[11px] text-[var(--ivory)]/60 border hairline-gold rounded-full px-3 py-1 whitespace-nowrap">{s.format}</span>
                  </div>
                  <p className="font-body text-[var(--ivory)]/70 text-sm md:text-base leading-relaxed">{s.desc}</p>
                  <div className="font-italic-display text-[var(--gold)] text-lg mt-3" data-testid={`mentoring-price-${i}`}>{s.price}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 border hairline bg-[var(--ink-soft)] p-8 md:p-14 grid md:grid-cols-12 gap-10 reveal">
        <div className="md:col-span-4">
          <div className="font-eyebrow text-[12px] text-[var(--gold)] mb-6">— Request a Place</div>
          <h3 className="font-display text-[var(--ivory)] text-3xl md:text-4xl leading-tight mb-6">Tell Reagen where you are.</h3>
          <p className="font-body text-[var(--ivory)]/70 text-base leading-relaxed">
            Share your stage, the area you want to develop and whether you'd prefer to meet online or
            at the atelier. Practical subjects are taught in person; strategy and review sessions are
            held online, wherever you are in the world.
          </p>
        </div>
        <div className="md:col-span-8">
          <MentoringForm />
        </div>
      </div>
    </div>
  </section>
);

/* ---------- Instagram strip ---------- */
const Instagram = () => {
  const imgs = [
    "/brand/campaign6.jpg",
    "/brand/campaign2.jpg",
    "/brand/campaign7.jpg",
    "/brand/campaign3.jpg",
    "/brand/campaign8.jpg",
    "/brand/campaign4.jpg",
    "/brand/campaign9.jpg",
    "/brand/campaign5.jpg",
    "/brand/campaign10.jpg",
    "/brand/campaign1.jpg",
  ];
  return (
    <section id="instagram" data-testid="instagram-section" className="py-28 md:py-32 px-6 md:px-16">
      <div className="max-w-[1480px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6 reveal">
          <div>
            <div className="font-eyebrow text-[13px] text-[var(--gold)] mb-5">— Follow the Atelier</div>
            <h2 className="font-display text-[var(--ivory)] text-4xl md:text-6xl leading-[0.95]">
              <span className="font-italic-display text-[var(--gold)]">{INSTAGRAM_HANDLE}</span>
            </h2>
          </div>
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="instagram-cta-btn"
            className="btn-luxe self-start md:self-end"
          >
            <i className="fa-brands fa-instagram text-base" />
            Open on Instagram
          </a>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 reveal">
          {imgs.map((src, i) => (
            <a
              key={i}
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noreferrer"
              data-testid={`ig-tile-${i}`}
              className="editorial-frame aspect-square block relative group"
            >
              <img src={src} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-[rgba(72,60,50,0.4)] opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                <i className="fa-brands fa-instagram text-2xl text-[var(--ivory)]" />
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Enquiry form ---------- */
const Enquiry = React.forwardRef((_, ref) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    occasion: "",
    event_date: "",
    location: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and a brief note are required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setSubmitted(true);
      toast.success("Your enquiry has reached the atelier.");
      setForm({ name: "", email: "", phone: "", occasion: "", event_date: "", location: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="enquire" ref={ref} data-testid="enquiry-section" className="relative py-28 md:py-40 px-6 md:px-16 bg-[var(--ink)] border-t hairline">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(184,153,104,0.08),transparent_70%)] pointer-events-none" />
      <div className="max-w-[1200px] mx-auto relative grid md:grid-cols-12 gap-16">
        <div className="md:col-span-5 reveal">
          <div className="font-eyebrow text-[13px] text-[var(--gold)] mb-6">— Private Commissions</div>
          <h2 className="font-display text-[var(--ivory)] text-5xl md:text-7xl leading-[0.92] mb-8">
            Begin a <br />
            <span className="font-italic-display text-[var(--gold)]">conversation</span>.
          </h2>
          <p className="font-body text-[var(--ivory)]/80 text-lg md:text-xl leading-relaxed mb-10">
            The atelier accepts a small number of commissions each season. Share the occasion;
            Reagen will reply personally within a week.
          </p>
          <div className="space-y-5 font-body text-[var(--ivory)]/65">
            <div className="flex items-center gap-4">
              <i className="fa-solid fa-envelope text-[var(--gold)] w-5" />
              <a href="mailto:enquiries@reagenvarross.com" className="hover:text-[var(--gold)] transition-colors">enquiries@reagenvarross.com</a>
            </div>
            <div className="flex items-center gap-4">
              <i className="fa-solid fa-location-dot text-[var(--gold)] w-5" />
              <span>By appointment · London</span>
            </div>
            <div className="flex items-center gap-4">
              <i className="fa-brands fa-instagram text-[var(--gold)] w-5" />
              <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="hover:text-[var(--gold)] transition-colors">{INSTAGRAM_HANDLE}</a>
            </div>
          </div>
        </div>

        <div className="md:col-span-7 reveal">
          {submitted ? (
            <div data-testid="enquiry-success" className="border hairline-gold p-12 md:p-16 text-center">
              <i className="fa-solid fa-feather text-[var(--gold)] text-3xl mb-6" />
              <h3 className="font-display text-[var(--ivory)] text-3xl md:text-4xl mb-4">Thank you.</h3>
              <p className="font-body text-[var(--ivory)]/70 max-w-md mx-auto">
                Your note has reached the atelier. Reagen will respond personally — please allow up to seven days.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="btn-ghost mt-8"
                data-testid="enquiry-reset-btn"
              >
                Send another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} noValidate data-testid="enquiry-form" className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <div className="md:col-span-1">
                <label className="font-eyebrow text-[12px] text-[var(--ivory)]/55">Name *</label>
                <input data-testid="enq-name" className="input-luxe" value={form.name} onChange={change("name")} placeholder="Your full name" required />
              </div>
              <div className="md:col-span-1">
                <label className="font-eyebrow text-[12px] text-[var(--ivory)]/55">Email *</label>
                <input data-testid="enq-email" type="email" className="input-luxe" value={form.email} onChange={change("email")} placeholder="you@domain.com" required />
              </div>
              <div>
                <label className="font-eyebrow text-[12px] text-[var(--ivory)]/55">Telephone</label>
                <input data-testid="enq-phone" className="input-luxe" value={form.phone} onChange={change("phone")} placeholder="+44 ..." />
              </div>
              <div>
                <label className="font-eyebrow text-[12px] text-[var(--ivory)]/55">Location</label>
                <input data-testid="enq-location" className="input-luxe" value={form.location} onChange={change("location")} placeholder="City, Country" />
              </div>
              <div>
                <label className="font-eyebrow text-[12px] text-[var(--ivory)]/55">Occasion</label>
                <input data-testid="enq-occasion" className="input-luxe" value={form.occasion} onChange={change("occasion")} placeholder="Wedding, Gala, Couture..." />
              </div>
              <div>
                <label className="font-eyebrow text-[12px] text-[var(--ivory)]/55">Event Date</label>
                <input data-testid="enq-date" className="input-luxe" value={form.event_date} onChange={change("event_date")} placeholder="Month / Year" />
              </div>
              <div className="md:col-span-2">
                <label className="font-eyebrow text-[12px] text-[var(--ivory)]/55">Your vision *</label>
                <textarea
                  data-testid="enq-message"
                  className="input-luxe min-h-[120px] resize-none"
                  value={form.message}
                  onChange={change("message")}
                  placeholder="Tell Reagen about the moment, the mood, the silhouette in your imagination..."
                  required
                />
              </div>
              <div className="md:col-span-2 flex flex-col sm:flex-row gap-6 items-start sm:items-center justify-between pt-4">
                <p className="font-body text-[var(--ivory)]/45 text-xs italic">
                  Enquiries are held in absolute confidence.
                </p>
                <button type="submit" disabled={submitting} data-testid="enq-submit-btn" className="btn-luxe btn-glow disabled:opacity-50">
                  {submitting ? "Sending..." : "Send to the Atelier"}
                  <i className="fa-solid fa-feather" />
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
});
Enquiry.displayName = "Enquiry";

/* ---------- Footer ---------- */
const Footer = () => (
  <footer data-testid="site-footer" className="px-6 md:px-16 py-16 border-t hairline">
    <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-10 items-end">
      <div className="md:col-span-6">
        <img src="/brand/wordmark_ivory.png" alt="Reagen Varross London" className="h-10 md:h-12 w-auto" />
        <p className="font-body text-[var(--ivory)]/55 mt-6 max-w-md">
          A British house of haute couture. Founded MMXVIII. London.
        </p>
      </div>
      <div className="md:col-span-3 font-eyebrow text-[13px] text-[var(--ivory)]/55 space-y-3">
        <div>enquiries@reagenvarross.com</div>
        <div>By appointment only</div>
      </div>
      <div className="md:col-span-3 flex md:justify-end gap-6 items-center">
        <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" data-testid="footer-instagram" className="text-[var(--ivory)]/70 hover:text-[var(--gold)] transition-colors">
          <i className="fa-brands fa-instagram text-xl" />
        </a>
        <a href="mailto:enquiries@reagenvarross.com" data-testid="footer-email" className="text-[var(--ivory)]/70 hover:text-[var(--gold)] transition-colors">
          <i className="fa-solid fa-envelope text-lg" />
        </a>
      </div>
    </div>
    <div className="max-w-[1400px] mx-auto mt-12 pt-6 border-t hairline flex flex-col md:flex-row justify-between gap-3 font-eyebrow text-[12px] text-[var(--ivory)]/40">
      <div>© {new Date().getFullYear()} Reagen Varross. All rights reserved.</div>
      <div>Site composed with quiet intent.</div>
    </div>
  </footer>
);

/* ---------- Home page ---------- */
const Home = () => {
  useReveal();
  const enquiryRef = useRef(null);
  const scrollToEnquiry = () => {
    document.getElementById("enquire")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <div className="grain min-h-screen">
      <Nav onEnquire={scrollToEnquiry} />
      <Hero onEnquire={scrollToEnquiry} />
      <PressBar />
      <Atelier />
      <PrimaMora onEnquire={scrollToEnquiry} />
      <Collection />
      <Press />
      <RedCarpet />
      <Voices />
      <Mentoring />
      <Instagram />
      <Enquiry ref={enquiryRef} />
      <Footer />
      <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#564940", color: "#f1ead8", border: "1px solid rgba(201,185,160,0.4)", fontFamily: "Spectral, serif" } }} />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
